import React from "react";
import { ArrowRight, UserSearch } from "lucide-react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";

const PrimaryButtonComponent = PrimaryButton as React.ComponentType<any>;
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>;

interface ProspectEmptyStateProps {
  onRetry: () => void;
  onNewSearch: () => void;
  /** Broader searches the AI suggested for this query; one click runs one. */
  suggestions?: string[];
  onPickSuggestion?: (query: string) => void;
}

export default function ProspectEmptyState({ onRetry, onNewSearch, suggestions = [], onPickSuggestion }: ProspectEmptyStateProps) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 flex flex-col items-center justify-center text-center shadow-xs">
      <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 mb-3">
        <UserSearch size={22} />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">No contacts matched this query yet</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mb-4">
        Try broadening your search keywords, using common job titles, or adjusting geographic filters.
      </p>
      {suggestions.length > 0 && onPickSuggestion && (
        <div className="flex w-full max-w-lg flex-col gap-2 mb-5 text-left">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Try a broader search</span>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onPickSuggestion(suggestion)}
              className="group flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-800 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:border-secondary hover:text-secondary transition-colors"
            >
              <span>{suggestion}</span>
              <ArrowRight size={14} className="shrink-0 text-gray-400 group-hover:text-secondary" />
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <PrimaryButtonComponent startContent={false} onClick={onRetry}>
          Try Again
        </PrimaryButtonComponent>
        <SecondaryButtonComponent title="New Search" onClick={onNewSearch} />
      </div>
    </div>
  );
}
